import { Injectable } from '@angular/core';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { Apollo, gql } from 'apollo-angular';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface AuthenticatedSession {
  email: string;
  idToken: string;
}

export type PortalRole = 'PLATFORM_ADMIN' | 'SITE_ADMIN' | 'SITE_MEMBER';

export interface CurrentUserProfile {
  _id: string;
  email: string;
  role: PortalRole;
  siteIds: string[];
}

const ME_QUERY = gql`
  query MeProfile {
    me {
      _id
      email
      role
      siteIds
    }
  }
`;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly userPool = new CognitoUserPool({
    UserPoolId: environment.cognito.userPoolId,
    ClientId: environment.cognito.clientId,
  });

  private readonly sessionSubject = new BehaviorSubject<AuthenticatedSession | null>(null);
  readonly session$ = this.sessionSubject.asObservable();

  private readonly profileSubject = new BehaviorSubject<CurrentUserProfile | null>(null);
  readonly profile$ = this.profileSubject.asObservable();
  readonly isPlatformAdmin$ = this.profile$.pipe(map((p) => p?.role === 'PLATFORM_ADMIN'));
  readonly canManageUsers$ = this.profile$.pipe(
    map((p) => p?.role === 'PLATFORM_ADMIN' || p?.role === 'SITE_ADMIN'),
  );

  private resolveReady!: () => void;
  readonly ready: Promise<void> = new Promise((resolve) => {
    this.resolveReady = resolve;
  });

  constructor(private readonly apollo: Apollo) {
    this.restoreSession();
  }

  get currentSession(): AuthenticatedSession | null {
    return this.sessionSubject.getValue();
  }

  isAuthenticated(): boolean {
    return this.currentSession !== null;
  }

  login(email: string, password: string): Promise<AuthenticatedSession> {
    const cognitoUser = new CognitoUser({ Username: email, Pool: this.userPool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session: CognitoUserSession) => {
          const authSession = this.sessionFromCognito(email, session);
          this.sessionSubject.next(authSession);
          this.loadProfile();
          resolve(authSession);
        },
        onFailure: (err: any) => reject(err),
        newPasswordRequired: () => {
          reject(
            new Error(
              'This account still requires a password to be set via the invite link. Check your email for the invite.',
            ),
          );
        },
      });
    });
  }

  logout(): void {
    const user = this.userPool.getCurrentUser();
    if (user) {
      user.signOut();
    }
    this.sessionSubject.next(null);
    this.profileSubject.next(null);
    this.apollo.client.clearStore().catch(() => {});
  }

  getIdToken(): Observable<string | null> {
    const user = this.userPool.getCurrentUser();
    if (!user) {
      return of(null);
    }
    return from(
      new Promise<string | null>((resolve) => {
        user.getSession((err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session || !session.isValid()) {
            resolve(null);
            return;
          }
          resolve(session.getIdToken().getJwtToken());
        });
      }),
    );
  }

  private restoreSession(): void {
    const user = this.userPool.getCurrentUser();
    if (!user) {
      this.resolveReady();
      return;
    }
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        this.sessionSubject.next(null);
        this.resolveReady();
        return;
      }
      user.getUserAttributes((attrErr: any, attributes: any) => {
        const email = attributes?.find((a: CognitoUserAttribute) => a.getName() === 'email')?.getValue()
          ?? session.getIdToken().payload['email'];
        this.sessionSubject.next({
          email,
          idToken: session.getIdToken().getJwtToken(),
        });
        this.loadProfile();
        this.resolveReady();
      });
    });
  }

  private sessionFromCognito(email: string, session: CognitoUserSession): AuthenticatedSession {
    return {
      email,
      idToken: session.getIdToken().getJwtToken(),
    };
  }

  private loadProfile(): void {
    this.apollo
      .query<{ me: CurrentUserProfile }>({ query: ME_QUERY, fetchPolicy: 'network-only' })
      .subscribe({
        next: ({ data }) => this.profileSubject.next(data?.me ?? null),
        error: () => this.profileSubject.next(null),
      });
  }
}
